import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { PW_ADVERT_CONTAINER } from 'utilities/consts/variable-types.js';
import { getFuseTag } from 'utilities/fuse.js';
import { getWindow } from 'utilities/window.js';
import { IAdvertFuseProps } from '../types.js';
import { FuseAdCallbackHook } from './fuse-ad-rendered-cb-hook.js';
import { FuseLoggerHook } from './fuse-log-hook.js';

export const AdvertFuseRequest = ({
  fuseTag,
  adUnitPath,
  origin,
  adDisplayTrueCB,
  children,
  fuseDataAttr,
}: Pick<IAdvertFuseProps, 'fuseTag' | 'adUnitPath' | 'origin' | 'adDisplayTrueCB' | 'forceMrecSpace'> & {
  children: JSX.Element;
  fuseDataAttr: Record<string, string>;
}) => {
  const adElementRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  /** Manages callbacks for onslotRenderede */
  const { logger } = FuseLoggerHook({ fuseTag, adUnitPath, origin, fuseDataAttr });

  FuseAdCallbackHook({ fuseTag, adUnitPath, adDisplayTrueCB, logger });

  useEffect(() => {
    /**  Make request to fuse for ad */
    if (fuseTag) {
      const fusetagLib = getFuseTag();
      setTimeout(() => {
        fusetagLib.que.push(() => {
          logger('Ad Request');
          /** https://docs.publift.com/fuse-web/technical-reference/zones/zone-styling/index.html */
          fusetagLib.registerZone(fuseTag);
        });
      });
    }
  }, [fuseTag, logger, origin]);

  /**
   * When fuseTagID changes remove all the ad content as these will pile up on top of each other
   * Remove ad element if user goes to different page
   */
  useEffect(() => {
    const adContainer = adElementRef.current?.firstChild;
    return () => {
      if (adContainer && ['adv-banner'].includes(origin)) {
        /** Clean up old ads on ad banner when navigating */
        while (adContainer.lastChild) {
          adContainer.removeChild(adContainer.lastChild);
        }
      }
    };
  }, [pathname, origin]);

  if (fuseTag) {
    const playwrightSettings = getWindow()?.playwright;
    const targetingString = JSON.stringify(playwrightSettings?.targeting);
    const hidePWAdsArr = playwrightSettings?.maskedAdsHide ?? [];
    /** If ad is displaying and is playwright hard code height so we dont get flickering of ad sizes */
    if (
      playwrightSettings?.isPlaywright &&
      playwrightSettings?.maskAds &&
      !hidePWAdsArr.find((hidePWAd) => hidePWAd.includes(origin))
    ) {
      return (
        <div
          data-testid={PW_ADVERT_CONTAINER}
          style={{ height: '250px', minWidth: '20px', backgroundColor: '#777070', color: 'white' }}
        >
          <div style={{ paddingTop: '60px' }}>
            {adUnitPath && <span className="u-block">Path: {adUnitPath}</span>}
            <span className="u-block">FuseTag: {fuseTag}</span>
            {targetingString && <span className="u-block">Targeting: {targetingString} </span>}
          </div>
        </div>
      );
    }
    /**
     * Note: HTMLComment causes the ad to break when loaded in a modal/portal
     *  <HTMLComment text={adUnitPath} />
     */
    return (
      <div data-testid={PW_ADVERT_CONTAINER} ref={adElementRef}>
        {children}
      </div>
    );
  }
  return null;
};
// advert-fuse.jsx:60 [ADVERT FUSE] 21834192510 / /9734223/NewSite_TopTile / [LOAD]
// advert-fuse.jsx:60 [ADVERT FUSE] 21825811305 / /9734223/HPBackground_NewSite_V2 / [LOAD]
// advert-fuse.jsx:60 [ADVERT FUSE] 21816204874 / /9734223/SideBanner_NewSite_V2 / [LOAD]
// advert-fuse.jsx:60 [ADVERT FUSE] 21816129188 / /9734223/SideBanner2_NewSite_V2 / [LOAD]

// advert-fuse.jsx:83 /9734223/LeftSkin_NewSite_V2
// advert-fuse.jsx:84 targeting:type = rural
